/**
 * CSV, as banks actually export it.
 *
 * Deliberately not a general-purpose CSV library. It handles the three things
 * that break naive parsers on real statements — quoted fields containing the
 * delimiter, semicolon-delimited files, and continental number formatting —
 * and nothing else.
 */

/**
 * Guesses the delimiter from the header line.
 *
 * Polish and German exports use `;` precisely because `,` is the decimal
 * separator there, so assuming a comma turns every amount into two columns.
 */
export function detectDelimiter(text) {
  /*
   * The widest row among the first lines, not the first line alone: a bank
   * export can open with its name on a line of its own ("Bank Millennium"),
   * and that line has no delimiter to vote with -- the whole semicolon table
   * was then read as one comma-split column.
   */
  const head = String(text).split(/\r?\n/).filter(line => line.trim()).slice(0, 30);
  const counts = [',', ';', '\t', '|'].map(delimiter => ({
    delimiter,
    // Count only outside quotes, so "Smith, John" does not vote for a comma.
    count: Math.max(0, ...head.map(line => (line.replace(/"[^"]*"/g, '').match(new RegExp(`\\${delimiter}`, 'g')) || []).length))
  }));
  const best = counts.sort((a, b) => b.count - a.count)[0];
  return best.count > 0 ? best.delimiter : ',';
}

/**
 * Parses CSV into rows of strings.
 *
 * A quoted field may contain the delimiter, a newline, or a doubled quote —
 * which is why this is a character loop and not a `split`.
 */
export function parseCsv(text, delimiter = null) {
  return readCsv(text, delimiter).rows;
}

/**
 * Parses CSV, keeping each row's line number in the original file.
 *
 * The line numbers are the point. Blank lines are dropped and a real export
 * opens with pages of preamble, so a row's position among the parsed rows says
 * nothing about where to find it in the file -- and "row 30 could not be read"
 * is only useful if row 30 is where the reader is looking.
 *
 * A quoted field may contain the delimiter, a newline, or a doubled quote --
 * which is why this is a character loop and not a `split`.
 */
export function readCsv(text, delimiter = null) {
  const sep = delimiter || detectDelimiter(text);
  const source = String(text).replace(/^\ufeff/, '');   // strip a BOM

  const rows = [];
  const lines = [];
  let row = [];
  let field = '';
  let quoted = false;
  let line = 1;
  let rowStart = 1;

  const endRow = () => {
    row.push(field);
    // A blank line is separation, not a row of one empty field.
    if (row.some(cell => cell.trim())) {
      rows.push(row.map(cell => cell.trim()));
      lines.push(rowStart);
    }
    row = [];
    field = '';
  };

  for (let i = 0; i < source.length; i++) {
    const char = source[i];

    if (quoted) {
      if (char === '"') {
        if (source[i + 1] === '"') { field += '"'; i++; }
        else quoted = false;
      } else {
        // A newline inside quotes stays in the value but still counts.
        if (char === '\n') line += 1;
        field += char;
      }
      continue;
    }

    if (char === '"') { quoted = true; continue; }
    if (char === sep) { row.push(field); field = ''; continue; }

    if (char === '\n' || char === '\r') {
      if (char === '\r' && source[i + 1] === '\n') i++;
      endRow();
      line += 1;
      rowStart = line;
      continue;
    }

    field += char;
  }

  endRow();
  return { rows, lines };
}

/**
 * Reads an amount written in any of the conventions a bank might use.
 *
 * `1 234,56`, `1,234.56`, `1234.56`, `-99,00`, `(99.00)` and `99,00 PLN` all
 * mean what you would expect. Returns null when there is no number in there at
 * all, so a header row or a blank cell can be told apart from a zero.
 */
/*
 * Which mark is the decimal point in a column of amounts, from the column
 * itself: "1,234.56" or "-45.10" say a dot, "1.234,56" or "45,10" a comma.
 * Needed for the figures that cannot say it alone -- "1,250" is 1250 in an
 * American export and 1.25 in a Polish one -- and null when nothing in the
 * column tells.
 */
export function detectDecimal(values) {
  let dot = 0;
  let comma = 0;
  for (const value of values) {
    const text = String(value == null ? '' : value).replace(/[^\d.,]/g, '');
    if (/\.\d{1,2}$/.test(text) || /,\d{3}\./.test(text)) dot += 1;
    if (/,\d{1,2}$/.test(text) || /\.\d{3},/.test(text)) comma += 1;
  }
  if (dot && !comma) return '.';
  if (comma && !dot) return ',';
  return null;
}

export function parseAmount(value, decimal = null) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  let text = String(value == null ? '' : value).trim();
  if (!text) return null;

  // Accountants' parentheses mean negative.
  const parenthesised = /^\((.*)\)$/.test(text);
  if (parenthesised) text = text.replace(/^\(|\)$/g, '');

  const negative = parenthesised || text.startsWith('-') || text.startsWith('−');

  // Drop everything that is not a digit or a separator: currency codes,
  // symbols, non-breaking spaces used as thousands separators.
  const cleaned = text.replace(/[^\d.,]/g, '');
  if (!cleaned) return null;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  let normalised;
  if (lastComma === -1 && lastDot === -1) {
    normalised = cleaned;
  } else if (decimal === '.' && lastDot === -1) {
    // A dot-decimal column: the commas here group thousands -- 1,250 is 1250.
    normalised = cleaned.replace(/,/g, '');
  } else if (decimal === ',' && lastComma === -1) {
    // A comma-decimal column: the dots here group thousands -- 1.250 is 1250.
    normalised = cleaned.replace(/\./g, '');
  } else if (lastComma > lastDot) {
    // Comma is the decimal separator: 1.234,56
    normalised = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    // Dot is the decimal separator: 1,234.56
    normalised = cleaned.replace(/,/g, '');
  }

  const amount = parseFloat(normalised);
  if (!Number.isFinite(amount)) return null;
  return negative ? -Math.abs(amount) : amount;
}

/*
 * Day first or month first, from a whole column of dates: one "15/10/2026"
 * settles it as day first, one "10/15/2026" as month first. American banks
 * write month first, and reading their exports day first put 4 March in
 * April and turned 15 October into a month 15. A column that never says
 * (every day 12 or under) takes `fallback` -- the app passes month first
 * for an American browser.
 */
export function detectDateOrder(values, fallback = 'dmy') {
  let dayFirst = 0;
  let monthFirst = 0;
  for (const value of values) {
    const match = String(value == null ? '' : value).match(/(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
    if (!match) continue;
    if (Number(match[1]) > 12) dayFirst += 1;
    if (Number(match[2]) > 12) monthFirst += 1;
  }
  if (dayFirst && !monthFirst) return 'dmy';
  if (monthFirst && !dayFirst) return 'mdy';
  return fallback === 'mdy' ? 'mdy' : 'dmy';
}

const monthOf = (year, month) => {
  const m = Number(month);
  return m >= 1 && m <= 12 ? `${year}-${String(m).padStart(2, '0')}` : null;
};

/**
 * `2026-08-14`, `14.08.2026`, `14/08/2026` and `2026/08/14` all become a
 * month; `08/14/2026` too when `order` is 'mdy'. A month outside 1-12 is
 * no month at all, rather than a row filed under "2026-15".
 */
export function parseMonth(value, order = 'dmy') {
  const text = String(value == null ? '' : value).trim();
  if (!text) return null;

  const iso = text.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (iso) return monthOf(iso[1], iso[2]);

  const short = text.match(/(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
  if (short) return monthOf(short[3], order === 'mdy' ? short[1] : short[2]);

  const yearMonth = text.match(/^(\d{4})[-/.](\d{1,2})$/);
  if (yearMonth) return monthOf(yearMonth[1], yearMonth[2]);

  return null;
}

/**
 * Guesses which column is which from the header names.
 *
 * A guess, offered for correction — the mapping is shown before anything is
 * imported, because getting it wrong writes hundreds of rows.
 */
export function guessColumns(header) {
  /*
   * Diacritics are stripped, so a Polish header has to be matched by what
   * survives that: "Tytul" becomes "tytu", "ksiegowania" becomes "ksigowania".
   */
  const normalise = name => String(name).toLowerCase().replace(/[^a-z]/g, '');

  /*
   * Needle order is the priority, not column order. A real export offers
   * several columns that could pass for one field -- mBank has both a booking
   * date and a transaction date, and both a transaction type and a payee -- so
   * the first NEEDLE that matches anywhere wins, rather than the leftmost
   * column that matches anything.
   */
  const find = (...needles) => {
    for (const needle of needles) {
      const index = header.findIndex(name => normalise(name).includes(needle));
      if (index !== -1) return index;
    }
    return null;
  };

  const amount = find('kwota', 'amount', 'betrag', 'value', 'wartosc');
  /*
   * Money out and money in in two columns, the way HSBC, Santander or
   * Millennium ("Obciążenia" / "Uznania") write a statement. Looked for only
   * when there is no single amount column -- every row of such a file was
   * left out as having no amount.
   */
  const debit = amount === null ? find('moneyout', 'paidout', 'obci', 'debit', 'withdrawal', 'wypyw') : null;
  const credit = amount === null ? find('moneyin', 'paidin', 'uznani', 'credit', 'deposit', 'wpyw') : null;
  const split = debit !== null && credit !== null && debit !== credit;

  return {
    // The date the money actually moved, ahead of the date the bank booked it.
    date: find('dataoperacji', 'datyoperacji', 'transactiondate', 'date', 'data', 'datum'),
    /*
     * The payee first. A statement's "description" column is often the type of
     * transaction -- forty rows reading CARD PURCHASE tell you nothing, while
     * the title column has the shop.
     */
    label: find('tytu', 'title', 'payee', 'description', 'opis', 'nazwa', 'odbiorca'),
    /*
     * The runner-up, used only when the chosen column is blank on a row. On a
     * card statement the title carries the shop and the description carries the
     * transaction type -- but a transfer in has no shop, and leaving that row
     * nameless loses the one entry the reader is most likely looking for.
     */
    labelAlt: find('description', 'opis', 'nazwa', 'title', 'tytu', 'payee', 'odbiorca'),
    amount,
    debit: split ? debit : null,
    credit: split ? credit : null,
    currency: find('currency', 'waluta', 'ccy'),
    /*
     * What the row actually was, when the description holds a category instead.
     *
     * This app's own export writes both -- the category under `description` and
     * the shop under `note` -- and the importer knew nothing about the second
     * column, so exporting a ledger and reading it straight back lost every
     * note. Worse than the loss: the note is what tells two rows apart, so the
     * duplicate check stopped recognising them and the month came back doubled.
     */
    note: find('note', 'notatka', 'uwagi', 'memo', 'remark'),
    /* The bank's own category (Chase, mBank): a fallback when the payee is unknown -- see classify. */
    category: (() => {
      const index = header.findIndex(name => ['category', 'kategoria', 'kategoriatransakcji'].includes(normalise(name)));
      return index === -1 ? null : index;
    })(),
    /*
     * Whether the payment went through. Revolut and other app banks list a
     * reverted or declined card payment with its amount like any other, and
     * importing it counted spending that never happened. Matched whole --
     * "stan" alone would catch a Polish balance column ("stan rachunku").
     */
    status: (() => {
      const index = header.findIndex(name => ['state', 'status', 'statustransakcji', 'statusoperacji'].includes(normalise(name)));
      return index === -1 ? null : index;
    })()
  };
}

/** In minus out, from a statement that keeps them apart; null when neither holds a figure. */
function splitAmount(out, into, decimal = null) {
  const paid = parseAmount(out, decimal);
  const got = parseAmount(into, decimal);
  if (paid === null && got === null) return null;
  return (got === null ? 0 : Math.abs(got)) - (paid === null ? 0 : Math.abs(paid));
}

/** A row the bank marks as never having gone through. */
const VOID_STATUS = /^(reverted|declined|failed|cancell?ed|rejected|odrzucon|anulowan|cofni)/i;

/**
 * Finds the row the table actually starts on.
 *
 * A bank export is not a CSV file with a header on line one. It opens with the
 * bank's address, the account number and a summary block, and the real header
 * turns up twenty-odd lines in -- so reading row zero as the header maps every
 * column to nothing and the whole statement lands as unusable.
 *
 * A row counts as the header when it names both a date and an amount, in
 * different columns. Summary blocks name one or the other, never both.
 */
export function findHeaderRow(rows, limit = 60) {
  const end = Math.min(rows.length, limit);
  for (let index = 0; index < end; index++) {
    const guess = guessColumns(rows[index]);
    const hasAmount = (guess.amount !== null && guess.date !== guess.amount) || guess.debit !== null;
    if (guess.date !== null && hasAmount) {
      return index;
    }
  }
  return 0;
}

/**
 * Turns parsed rows plus a column mapping into entries the API accepts.
 *
 * @param from   index of the first data row (the row after the header)
 * @param lines  each row's line number in the file, from `readCsv`
 */
export function rowsToEntries(rows, mapping, { defaultCurrency, from = 1, lines = null, dateOrder = 'dmy' }) {
  const body = rows.slice(from);
  /* Read from the whole file, not row by row: a single row cannot tell 03/04 from 04/03, nor 1,250 from 1.25. */
  const order = detectDateOrder(body.map(cells => cells[mapping.date]), dateOrder);
  const decimal = detectDecimal(body.flatMap(cells => [mapping.amount, mapping.debit, mapping.credit]
    .filter(column => column != null).map(column => cells[column])));

  return body.map((cells, index) => {
    /* Either one signed column, or money out and money in side by side -- see guessColumns. */
    const amount = mapping.amount == null && mapping.debit != null && mapping.credit != null
      ? splitAmount(cells[mapping.debit], cells[mapping.credit], decimal)
      : parseAmount(cells[mapping.amount], decimal);
    const month = parseMonth(cells[mapping.date], order);
    const label = String(cells[mapping.label] ?? '').trim()
      || (mapping.labelAlt == null ? '' : String(cells[mapping.labelAlt] ?? '').trim());
    const currency = mapping.currency == null
      ? defaultCurrency
      : (String(cells[mapping.currency] ?? '').trim().toUpperCase() || defaultCurrency);

    const note = mapping.note == null
      ? ''
      : String(cells[mapping.note] ?? '').trim();

    const status = mapping.status == null ? '' : String(cells[mapping.status] ?? '').trim();
    const bankCategory = mapping.category == null ? '' : String(cells[mapping.category] ?? '').trim();

    return {
      line: lines ? lines[from + index] : from + index + 1,
      month,
      /** Reverted, declined, failed: listed by the bank, but no money moved. */
      voided: VOID_STATUS.test(status),
      label: label || '—',
      bankCategory,
      /** What the row was, when the label is a category. Empty when there is none. */
      note,
      amount: amount === null ? null : Math.abs(amount),
      currency,
      // Banks write money out as negative and money in as positive.
      kind: amount !== null && amount < 0 ? 'expense' : 'income',
      valid: amount !== null && amount !== 0 && month !== null
    };
  });
}

/** Escapes one CSV field: quote it when it contains anything structural. */
const escapeField = value => {
  const text = String(value == null ? '' : value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function toCsv(rows) {
  return rows.map(row => row.map(escapeField).join(',')).join('\r\n');
}
